import { CommonModule } from '@angular/common'
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { HubtypeUser } from 'models/hubtype-user'
import { IntegrationAuthEvent } from 'models/integration-event'
import { catchError, filter, first, of } from 'rxjs'
import { BaseModule } from 'shared/base/base.module'
import { LoginAction, LogoutAction } from 'src/app/actions/auth'
import { TwoFAService } from 'src/app/profile/user-settings/security-tab/services/two-fa.service'
import * as fromRoot from 'src/app/reducers'
import { State } from 'src/app/reducers'
import { IntegrationsService } from 'src/app/services/integrations.service'

@Component({
  selector: 'login-two-fa',
  templateUrl: './login-two-fa.component.html',
  styleUrl: './login-two-fa.component.scss',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, BaseModule],
  providers: [TwoFAService],
})
export class LoginTwoFaComponent implements OnInit {
  form = new FormGroup({
    code: new FormControl('', [Validators.required]),
  })

  isLoading = signal(false)
  error = signal('')

  private destroyRef = inject(DestroyRef)

  constructor(
    private twoFAService: TwoFAService,
    private store: Store<State>,
    private integrationsService: IntegrationsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const returnUrl = this.route.snapshot.queryParams['returnUrl']

    this.store
      .select(fromRoot.getMeState)
      .pipe(first(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe((me: HubtypeUser) => {
        if (returnUrl) {
          this.router.navigateByUrl(returnUrl)
        } else {
          const url = `/${me.preferred_product || 'desk'}`
          this.router.navigate([url])
        }
      })
  }

  onSubmit() {
    if (this.form.invalid) {
      return
    }

    this.isLoading.set(true)

    this.twoFAService
      .confirmLogin(this.form.value.code)
      .pipe(
        catchError(error => {
          const errorResponse = error.error
          if (errorResponse.status === 400) {
            this.isLoading.set(false)
            this.error.set('Invalid code')

            return of(null)
          } else {
            // Other kind of error, we go back to login
            this.isLoading.set(false)
            this.onCancel()

            throw error
          }
        }),
        filter(Boolean)
      )
      .subscribe(res => {
        this.error.set(null)
        this.store.dispatch(new LoginAction(res))

        if (this.integrationsService.isUsingIntegrations()) {
          console.log('Integration: send authenticated event')
          this.integrationsService.sendEvent(new IntegrationAuthEvent(res))
        }
      })
  }

  onCancel() {
    this.store.dispatch(new LogoutAction())
    this.router.navigate(['/sign-in'])
  }
}
